import { z } from 'zod';
import { requiredFieldError } from '..';
import { FINANCIAL_PRODUCT } from '../constants';
import {
  employedDetails,
  income,
  incomeVerificationSchema,
  otherDetails,
  pensionerDetails,
  selfEmployedDetails,
} from '../shared';
import { ApplicantType, EmploymentType } from '../types';

/**
 * incomeVerificationFormSchema used in the form
 * Additional fields are required to be validated in the form hence a different validation rule is applied
 */
export const incomeVerificationFormSchema = incomeVerificationSchema.superRefine((val, ctx) => {
  const isVerificationRequired = () => {
    switch (val.financialProductId) {
      case FINANCIAL_PRODUCT.FLEX:
        return false;
      case FINANCIAL_PRODUCT.ELECTRIFY_2515:
        // Skip verification for cash applications
        return !('payment' in val && val?.payment?.isCashApplication);
      default:
        return 'usingOpenBanking' in val.applicant && !val.applicant.usingOpenBanking;
    }
  };

  if (val.applicant.employment) {
    switch (val.applicant.employment.type) {
      case EmploymentType.EMPLOYED:
        if (!val.applicant.employment.employed?.employerBusinessName) {
          return ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredFieldError(),
            path: ['applicant', 'employment', 'employed', 'employerBusinessName'],
          });
        }
        break;
      case EmploymentType.SELF_EMPLOYED:
        if (!val.applicant.employment.selfEmployed?.abn || !val.applicant.employment.selfEmployed?.entityName) {
          return ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredFieldError(),
            path: ['applicant', 'employment', 'selfEmployed', 'abn'],
          });
        }
        break;
      case EmploymentType.PENSIONER:
        if (!val.applicant.employment.pensioner?.pensionType) {
          return ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredFieldError(),
            path: ['applicant', 'employment', 'pensioner', 'pensionType'],
          });
        }
        break;
    }
  }

  // When income verification has FAILED, the user is proceeding via manual review. The uploaded
  // document IDs must be present in the payload before we submit — otherwise we'd call
  // updateApplication with no income documents. If none are present, block submission and ask for
  // documents instead. Uploaded documents that failed verification still count (they carry ids).
  if (val.incomeVerification === 'Failed' && !val.applicant?.income?.isSkipPrimaryIncome) {
    const primary = val.applicant?.income?.primary;
    const hasPrimaryDocuments =
      (primary?.documents?.length ?? 0) > 0 ||
      (primary?.documentIds?.length ?? 0) > 0 ||
      Boolean(primary?.bankStatementToken);

    if (!hasPrimaryDocuments) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Income verification documents are required.',
        path: ['applicant', 'income'],
      });
    }
  }

  if (isVerificationRequired()) {
    if (val.applicant?.income?.primary?.amount <= 0) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Amount must be greater than $0',
        path: ['applicant.income.primary.amount'],
      });
    }

    if (val.applicant?.income?.isAdditionalIncome && val.applicant?.income?.additional?.amount <= 0) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Amount must be greater than $0',
        path: ['applicant.income.additional.amount'],
      });
    }

    const customerDocumentIssue = {
      code: z.ZodIssueCode.custom,
      message: 'Income verification documents are required.',
      path: ['applicant', 'income'],
    };

    if (val.applicant?.income?.primary?.amount <= 0) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Amount must be greater than $0',
        path: ['applicant.income.primary.amount'],
      });
    }

    if (val.applicant.income?.isAdditionalIncome === undefined)
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredFieldError(),
        path: ['applicant', 'income', 'isAdditionalIncome'],
      });

    if (val.applicant?.income?.isAdditionalIncome && val.applicant?.income?.additional?.amount <= 0) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Amount must be greater than $0',
        path: ['applicant.income.additional.amount'],
      });
    }

    if (val.applicant?.income?.isAdditionalIncome && !val.applicant?.income?.isSkipAdditionalIncome) {
      const { bankStatementToken, documents } = val.applicant.income.additional;
      if (!bankStatementToken && documents?.length == 0) {
        return ctx.addIssue(customerDocumentIssue);
      }
    }

    if (val.applicant?.income?.primary?.bankStatementToken && val.applicant?.income?.primary?.documents) {
      const { bankStatementToken, documents } = val.applicant.income.primary;
      if (
        val.applicant?.income?.primary &&
        !val.applicant.income.isSkipPrimaryIncome &&
        !bankStatementToken &&
        documents?.length == 0 &&
        val.financialProductId !== FINANCIAL_PRODUCT.BRIGHTE_PAY
      ) {
        return ctx.addIssue(customerDocumentIssue);
      }
    }
  }
});

export type IncomeVerificationType = {
  financialProductId: string;
  applicant: {
    type: ApplicantType;
    employment: {
      type: EmploymentType;
      employed?: z.infer<typeof employedDetails>;
      selfEmployed?: z.infer<typeof selfEmployedDetails>;
      pensioner?: z.infer<typeof pensionerDetails>;
      other?: z.infer<typeof otherDetails>;
    };
    income: z.infer<typeof income>;
    financeAmount?: number;
    usingOpenBanking?: boolean;
  };
  payment?: {
    isCashApplication?: boolean;
  };
  incomeVerification?: string;
};
